import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Sidebar from '../components/Sidebar/Sidebar';
import { useTabs } from '../contexts/TabContext';
import useLocalStorageVO from '../components/Common/UseLocalStorageVO';
import { getMyMenuRoles } from '../api/menuRoleApi';

const SidebarContainer = ({ activeMenuItem, activeMenuGroup }) => {
  const { openTab, activeTab } = useTabs();
  const { loginUser } = useLocalStorageVO();

  // 고정된 메뉴 아이템 구조 (상태 변경 없음)
  const baseMenuItems = useMemo(() => [
    {
      id: 'main',
      name: '메인',
      icon: '🏠',
      isActive: false,
      subItems: [] // Main은 하위 메뉴 없음
    },
    {
      id: 'ci',
      name: '기준정보관리',
      icon: 'ℹ️',
      isActive: false,
      subItems: [
        { id: 'ci-common', name: '공통코드관리' },
        { id: 'ci-factory', name: '공장정보관리' },
        { id: 'ci-line', name: '라인정보관리' },
        { id: 'ci-equipment', name: '설비정보관리' },
        { id: 'ci-customer', name: '거래처관리' },
        { id: 'ci-warehouse', name: '창고정보관리' }
      ]
    },
    // 다른 메뉴 항목들...
    {
      id: 'pi',
      name: '품목정보관리',
      icon: '📋',
      isActive: false,
      subItems: [
        { id: 'pi-material-management', name: '원/부자재관리' },
        { id: 'pi-half-product', name: '반제품관리' },
        { id: 'pi-product', name: '제품관리' },
        { id: 'pi-bom', name: 'BOM관리' }
      ]
    },
    {
      id: 'sm',
      name: '영업관리',
      icon: '💼',
      isActive: false,
      subItems: [
        { id: 'sm-order', name: '주문등록' },
        { id: 'sm-sales', name: '출하관리' },
        { id: 'sm-transaction', name: '거래명세서관리' }
      ]
    },
    {
      id: 'mi',
      name: '자재/재고관리',
      icon: '📦',
      isActive: false,
      subItems: [
        { id: 'mi-inbound', name: '입고관리' },
        { id: 'mi-outbound', name: '출고관리' },
        { id: 'mi-stock', name: '자재/재고현황' },
        { id: 'mi-stock-history', name: '재고상세이력' }
      ]
    },
    {
      id: 'pm',
      name: '생산관리',
      icon: '🏭',
      isActive: false,
      subItems: [
        { id: 'mm-plan', name: '생산계획관리' },
        { id: 'mm-workorder', name: '작업지시관리' },
        { id: 'mm-result-in', name: '생산실적등록' },
        { id: 'mm-kpi-result-in', name: 'KPI생산실적등록' },
        { id: 'mm-result', name: '생산실적조회' },
        { id: 'mm-defect', name: '불량조회' }
      ]
    },
    {
      id: 'mo',
      name: '모니터링',
      icon: '📊',
      isActive: false,
      subItems: [
        { id: 'mo-kpi', name: 'KPI모니터링' },
      ]
    },
    {
      id: 'rp',
      name: '리포트',
      icon: '📝',
      isActive: false,
      subItems: [
        { id: 'rp-mpv', name: '계획대비실적조회' },
        { id: 'rp-ppr', name: '기간별생산실적' },
        { id: 'rp-dpr', name: '불량률 현황' },
        { id: 'rp-imr', name: '입출고현황' }
      ]
    },
    {
      id: 'sy',
      name: '시스템',
      icon: '⚙️',
      isActive: false,
      subItems: [
        { id: 'sy-notice', name: '공지사항' },
        { id: 'sy-authority', name: '권한관리' },
        { id: 'sy-user', name: '사용자관리' },
        { id: 'sy-company', name: '회사정보' },
        { id: 'sy-menu', name: '메뉴관리' },
        { id: 'sy-drive', name: '문서대장' },
        { id: 'sy-kpi', name: 'KPI 지표관리' },
        { id: 'sy-3d', name: '3D모델업로드' },
        { id: 'sy-sensor', name: '설비정보수정' }
      ]
    },
    {
      id: 'digital-twin',
      name: '디지털 트윈',
      icon: '🧊',
      isActive: false,
      subItems: []
    },
    {
      id: 'mov',
      name: '동영상 매뉴얼',
      icon: '📺',
      isActive: false,
      subItems: []
    },
    {
      id: 'group-purchase',
      name: '공동 구매',
      icon: '🛒',
      isActive: false,
      subItems: []
    },
  ], []);

  // 활성 메뉴 상태 관리
  const [activeMenuId, setActiveMenuId] = useState(null);
  
  // 권한 필터링된 메뉴 아이템들
  const [menuItems, setMenuItems] = useState(baseMenuItems);

  // 메뉴 권한(오픈 상태)에 따라 menuItems를 필터링
  useEffect(() => {
    async function fetchAndFilterMenus() {
      try {
        const rawRoles = await getMyMenuRoles();
        const roleList = rawRoles?.getMyMenuRoles || [];
        const openMenuIds = new Set(
            roleList
                .filter(role => role.isOpen === true)
                .map(role => (role.menuId || '').trim().toLowerCase())
        );

        setMenuItems(() => {
          let filtered = baseMenuItems
              .map(group => {
                const visibleSubItems = group.subItems?.filter(
                    sub => openMenuIds.has((sub.id || '').trim().toLowerCase())
                ) || [];
                const isGroupVisible = openMenuIds.has((group.id || '').trim().toLowerCase()) || visibleSubItems.length > 0;
                return isGroupVisible
                    ? {
                      ...group,
                      subItems: visibleSubItems,
                    }
                    : null;
              })
              .filter(Boolean);

          // 지부장 사용자의 경우 영업관리 메뉴 제외 (권한과 관계없이)
          if (loginUser.isBranchManagerLogin === true) {
            filtered = filtered.filter(item => item.id !== 'sm'); // 'sm'은 영업관리 메뉴 ID
          }

          // [중요] '디지털 트윈' 메뉴는 권한과 상관없이 항상 사이드바에 표시되도록 강제로 추가합니다.
          // 이미 있으면 중복 추가하지 않도록 체크합니다.
          const hasDigitalTwin = filtered.some(item => (item.id || '').toLowerCase() === 'digital-twin');
          if (!hasDigitalTwin) {
            filtered.push({
              id: 'digital-twin',
              name: '디지털 트윈',
              icon: '🧊',
              isActive: false,
              subItems: []
            });
          }
          
          // '동영상 매뉴얼' 메뉴도 권한과 상관없이 항상 표시
          const hasVideoManual = filtered.some(item => (item.id || '').toLowerCase() === 'mov');
          if (!hasVideoManual) {
            filtered.push({
              id: 'mov',
              name: '동영상 매뉴얼',
              icon: '📺',
              isActive: false,
              subItems: []
            });
          }
          
          return filtered;
        });
      } catch (e) {
        console.error('메뉴 권한 조회 실패:', e);
      }
    }

    fetchAndFilterMenus();
  }, [baseMenuItems, loginUser.isBranchManagerLogin]);

  // 현재 열린 메뉴 아이템 상태 (펼쳐진 상태 유지를 위함)
  const [expandedItems, setExpandedItems] = useState(['main']);

  // 메뉴 구조를 메모이제이션하여 재계산 방지
  const menuStructure = useMemo(() => {
    const structure = new Map();

    // 메뉴 구조를 맵으로 구성 - 권한 필터링된 menuItems 사용
    menuItems.forEach(item => {
      if (item.subItems && item.subItems.length > 0) {
        item.subItems.forEach(subItem => {
          structure.set(subItem.id, item.id);
        });
      }
    });

    return structure;
  }, [menuItems]);

  // 활성화된 메뉴 업데이트 함수
  const updateActiveMenu = useCallback((activeId) => {
    setActiveMenuId(activeId);
  }, []);

  // 현재 활성화된 탭이 변경될 때 해당 메뉴를 활성화
  useEffect(() => {
    if (activeTab) {
      updateActiveMenu(activeTab);

      // 상위 메뉴 그룹 확장
      const menuGroup = menuStructure.get(activeTab) || null;
      if (menuGroup) {
        setExpandedItems(prev => {
          if (!prev.includes(menuGroup)) {
            return [...prev, menuGroup];
          }
          return prev;
        });
      }
    }
  }, [activeTab, updateActiveMenu, menuStructure]);

  // 직접 activeMenuItem, activeMenuGroup props가 변경될 때도 적용
  useEffect(() => {
    if (activeMenuItem) {
      updateActiveMenu(activeMenuItem);
    }

    if (activeMenuGroup) {
      setExpandedItems(prev => {
        if (!prev.includes(activeMenuGroup)) {
          return [...prev, activeMenuGroup];
        }
        return prev;
      });
    }
  }, [activeMenuItem, activeMenuGroup, updateActiveMenu]);

  // 메뉴 아이템 확장/축소 토글
  const toggleItem = useCallback((id) => {
    setExpandedItems(prev =>
        prev.includes(id)
            ? prev.filter(itemId => itemId !== id)
            : [...prev, id]
    );
  }, []);

  // 메뉴 클릭 핸들러
  const handleItemClick = useCallback((id) => {
    // 탭 정보 생성
    let tabInfo = {
      id: id,
      name: '탭'
    };

    // 메인 메뉴 클릭 시 ID가 'main'으로 전달됨 (메뉴 아이템의 ID)
    if (id === 'main') {
      tabInfo.name = '메인';
      openTab(tabInfo);
      return;
    }

    // 메뉴 아이템 찾기 - 권한 필터링된 menuItems 사용
    let foundTab = false;
    menuItems.forEach(item => {
      if (item.id === id) {
        tabInfo.name = item.name;
        foundTab = true;
      } else if (item.subItems) {
        const subItem = item.subItems.find(sub => sub.id === id);
        if (subItem) {
          tabInfo.name = subItem.name;
          tabInfo.group = item.id;
          foundTab = true;
        }
      }
    });

    if (foundTab) {
      openTab(tabInfo);
    }
  }, [openTab, menuItems]);

  // 활성 상태 추가
  const finalMenuItems = useMemo(() => {
    // 이미 권한 필터링과 지부장 영업관리 메뉴 제외 처리가 완료된 menuItems에 활성 상태만 추가
    return menuItems.map(item => {
      const isActive = item.id === activeMenuId || 
        (item.subItems && item.subItems.some(subItem => subItem.id === activeMenuId));
      
      return { ...item, isActive };
    });
  }, [menuItems, activeMenuId]);

  return (
      <Sidebar
          items={finalMenuItems}
          expandedItems={expandedItems}
          onItemClick={handleItemClick}
          onToggleItem={toggleItem}
      />
  );
};

export default SidebarContainer;